import React, {
	memo,
	useCallback,
	useMemo,
	useRef,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Form,
	Input
} from 'semantic-ui-react';

import {
	maskValue,
	unmaskValue
} from '../../services/masks';
import {
	formatInputValue,
	unformatInputValue
} from '../../services/formatters';
import { log } from '../../services/utils';
import {
	floatKeyDown,
	integerKeyDown
} from '../../services/validation';

import DescriptionTooltip from './DescriptionTooltip';
import HelpModal from '../Shared/HelpModal';
import ValidationPopup from './ValidationPopup';

const FormInput = ( {
	align = 'left',
	errors = [],
	fieldLabel,
	fieldLabelAlignment = 'left',
	fieldLabelPosition = 'above',
	forceValidation,
	formatter,
	helpId,
	icon,
	inputLabel,
	inputLabelPosition = 'left',
	instructions,
	keyDown,
	mask,
	name,
	onBlur,
	onChange,
	onFocus,
	readOnly,
	required,
	spellcheck = true,
	type = 'text',
	value,
	width,
	...props
} ) => {
	const inputRef = useRef( null );

	const [ validate, setValidate ] = useState( false );
	const [ validationPopup, setValidationPopup ] = useState( false );

	const hasFocus = useCallback( () => inputRef.current === document.activeElement, [] );
	const isInvalid = useCallback(
		() => validate || forceValidation
			? errors.some( error => error.error )
			: false,
		[
			errors,
			forceValidation,
			validate
		]
	);

	const handleChange = useCallback( (
		e,
		{
			name,
			value
		}
	) => {
		// disable validation while user is entering a value
		if ( validate ) {
			setValidate( false );
		}

		// disable validationPopup while user is entering a value
		if ( validationPopup ) {
			setValidationPopup( false );
		}

		// unmask value
		const target = {
			name,
			value: mask
				? unmaskValue( mask, value )
				: value
		};

		// update formData
		onChange( { target } );
	}, [
		mask,
		onChange,
		validate,
		validationPopup
	] );

	const handleFocus = useCallback( e => {
		if (
			!validationPopup
			&& isInvalid()
			&& hasFocus()
		) {
			setValidationPopup( true );
		} else if ( validationPopup ) {
			setValidationPopup( false );
		}

		if ( onFocus ) {
			onFocus( e );
		}

		if ( formatter ) {
			const {
				name,
				value
			} = e.target;

			onChange( {
				target: {
					name,
					value: unformatInputValue( formatter, value )
				}
			} );
		}
	}, [
		formatter,
		hasFocus,
		isInvalid,
		onChange,
		onFocus,
		validationPopup
	] );

	const handleBlur = useCallback( e => {
		// enable validation when field loses focus
		if ( errors.length && !validate ) {
			setValidate( true );
		}

		// disable validationPopup when field loses focus
		if ( validationPopup ) {
			setValidationPopup( false );
		}

		if ( onBlur ) {
			onBlur( e );
		}

		if ( formatter ) {
			const {
				name,
				value
			} = e.target;

			onChange( {
				target: {
					name,
					value: formatInputValue( formatter, value )
				}
			} );
		}
	}, [
		errors.length,
		formatter,
		onBlur,
		onChange,
		validate,
		validationPopup
	] );

	const alignment = useMemo(
		() => align === 'right'
			? { marginLeft: 'auto' }
			: null,
		[ align ]
	);

	return (
		<>
			{ log( `- ${ name } input` ) }
			<Form.Field
				id={ name }
				error={ isInvalid() }
				required={ !readOnly && required }
				style={ alignment }
				width={ width }
			>
				{ fieldLabel && fieldLabelPosition === 'above'
					? <label style={ { textAlign: fieldLabelAlignment } }>
						{ helpId
							? <HelpModal
								id={ helpId }
								triggerPosition="left"
								triggerSize="small"
							/>
							: null
						}
						{ fieldLabel }
						{ instructions ? <>&nbsp;&nbsp;<DescriptionTooltip text={ instructions } /></> : null }
					</label>
					: null
				}
				<Input
					labelPosition={ inputLabelPosition }
					icon={ Boolean( icon ) }
					name={ name }
					onBlur={ handleBlur }
					onFocus={ handleFocus }
					onChange={ handleChange }
					onKeyDown={ keyDown === 'integer'
						? integerKeyDown
						: keyDown === 'float'
							? floatKeyDown
							: null
					}
					readOnly={ readOnly }
					spellCheck={ spellcheck }
					transparent={ readOnly }
					type={ type }
					value={ readOnly && !value
						? '--'
						: mask
							? maskValue( mask, value )
							: value
					}
					{ ...props }
					autoComplete="chrome-off"
				>
					{ !readOnly && inputLabelPosition === 'left'
						? inputLabel || null
						: null
					}
					<input ref={ inputRef } />
					{ !readOnly && inputLabelPosition === 'right'
						? inputLabel || null
						: null
					}
					{ !readOnly
						? icon || null
						: null
					}
				</Input>
				{
					fieldLabel && fieldLabelPosition === 'below'
						? <label
							style={ {
								fontSize: '0.85em',
								fontWeight: 300,
								lineHeight: '1.3',
								margin: '0.33333333rem 0em 1rem 0em',
								textAlign: fieldLabelAlignment
							} }>
							{ helpId
								? <HelpModal
									id={ helpId }
									triggerSize="small"
									triggerPosition="left"
								/>
								: null
							}
							{ fieldLabel }

							{ instructions ? <>&nbsp;&nbsp;<DescriptionTooltip text={ instructions } /></> : null }
						</label>
						: null
				}
			</Form.Field>
			<ValidationPopup
				context={ inputRef }
				errors={ errors }
				open={ validationPopup }
			/>
		</>
	);
};

FormInput.propTypes = {
	align: PropTypes.string,
	errors: PropTypes.arrayOf( PropTypes.shape( {
		error: PropTypes.bool,
		message: PropTypes.node
	} ) ),
	fieldLabel: PropTypes.node,
	fieldLabelAlignment: PropTypes.string,
	fieldLabelPosition: PropTypes.string,
	forceValidation: PropTypes.bool,
	formatter: PropTypes.string,
	helpId: PropTypes.number,
	icon: PropTypes.node,
	inputLabel: PropTypes.node,
	inputLabelPosition: PropTypes.oneOf( [
		'left',
		'right',
		'right corner',
		'left corner'
	] ),
	instructions: PropTypes.string,
	keyDown: PropTypes.oneOf( [
		'float',
		'integer'
	] ),
	mask: PropTypes.string,
	name: PropTypes.string.isRequired,
	onBlur: PropTypes.func,
	onChange: PropTypes.func,
	onFocus: PropTypes.func,
	readOnly: PropTypes.bool,
	required: PropTypes.bool,
	spellcheck: PropTypes.bool,
	type: PropTypes.oneOf( [
		'text',
		'number'
	] ),
	value: PropTypes.string,
	width: PropTypes.number
};

export default memo( FormInput );
